import React, { useMemo } from 'react';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useMediaQuery } from '@mui/material';
import type { FeatureFlag } from '@surfline/map-lib';
import { UserLocation, getUser } from '@surfline/web-common';
import theme from '@wavetrak/theme';
import dynamic from 'next/dynamic';
import type { WavetrakPageContext } from 'types/wavetrak';

import { usePageCall } from 'common/hooks/usePageCall';
import { useMinWidthTablet } from 'hooks/useMediaQueries';
import { getOrCreateStore } from 'stores';
import { useAppSelector } from 'stores/hooks';
import { ChartPageType, type ChartSlug } from 'types/mapTiler';
import Surftiler from 'containers/Surftiler';
import { getSurftilerFeatureFlags } from 'utils/surftiler';

const MapTilerCharts = dynamic(() => import('containers/MapTilerCharts'), {
  loading: () => <div />,
  ssr: false,
});

interface ChartsHomepageProps {
  chartPageType: ChartPageType;
  chartHomepageQueryParams: {
    type?: ChartSlug;
    locationAsPath?: string;
  };
  isSurftilerChartsEnabled: boolean;
  surftilerFeatureFlags?: FeatureFlag[];
}

const ChartsHomepage: NextPage<ChartsHomepageProps> = ({
  chartPageType,
  chartHomepageQueryParams,
  isSurftilerChartsEnabled,
  surftilerFeatureFlags,
}) => {
  const router = useRouter();
  const { chartLocation } = router.query as {
    chartLocation: string | undefined;
  };

  const segmentProperties = useMemo(
    () => ({
      category: 'charts-maptiler',
      path: router.asPath,
    }),
    // Important: Don't include router.asPath as a dependency here as it causes segementProperties to update
    // when the user moves the map or toggles layers or does anything that causes the url to update
    [],
  );
  const { location: userLocation } = useAppSelector(getUser) as { location: UserLocation };

  const userLong = userLocation.longitude || -118.46794972246397;
  const userTimezone = userLocation.time_zone || 'America/Los_Angeles';
  const isDesktop = useMediaQuery(theme.breakpoints.up('desktop'));
  const isTablet = useMinWidthTablet();

  const minZoom = useMemo(() => {
    let zoom = 0.5;
    if (isTablet) zoom = 1;
    if (isDesktop) zoom = 2;
    return zoom;
  }, [isTablet, isDesktop]);

  const location = chartLocation || `0,${userLong},${minZoom}`;

  usePageCall('Charts', segmentProperties, !!router);

  const metaData = {
    pageTitle: 'Global Wave Height Forecast Chart - Surfline',
    metaDescription:
      'Get 16 day global weather and surf forecasts in an interactive chart. See swell, wind, and sea temperature change dynamically over 16 days.',
    url: '',
  };

  if (isSurftilerChartsEnabled) {
    return <Surftiler featureFlags={surftilerFeatureFlags} metaData={metaData} />;
  }

  return (
    <MapTilerCharts
      chartPageType={chartPageType}
      chartQueryParams={chartHomepageQueryParams}
      timezone={userTimezone}
      location={location}
      chartPrefix="/surf-charts"
      metaData={metaData}
    />
  );
};

ChartsHomepage.getInitialProps = async (context: WavetrakPageContext) => {
  const store = getOrCreateStore();
  const isServer = !!context.req;
  const { query } = context;
  const queryParams = query as {
    type: ChartSlug;
    chartLocation: string;
    sl_web_surftiler?: string;
  };

  const isSurftilerChartsEnabled = !(
    queryParams.sl_web_surftiler && queryParams.sl_web_surftiler === 'off'
  );

  const surftilerFeatureFlags = isSurftilerChartsEnabled
    ? await getSurftilerFeatureFlags(context)
    : [];

  if (isServer) {
    return {
      chartHomepageQueryParams: queryParams,
      chartPageType: ChartPageType.HOMEPAGE,
      enableCssBaseline: true,
      enableTheme: true,
      isAppBannerEnabled: true,
      isChartsHomepage: true,
      isFooterVisible: false,
      librariesToLoad: {
        mapTiler: !isSurftilerChartsEnabled,
      },
      ssrReduxState: store.getState(),
      isSurftilerChartsEnabled,
      surftilerFeatureFlags,
    };
  }

  return {
    chartHomepageQueryParams: queryParams,
    chartPageType: ChartPageType.HOMEPAGE,
    enableCssBaseline: true,
    enableTheme: true,
    isAppBannerEnabled: true,
    isChartsHomepage: true,
    isFooterVisible: false,
    librariesToLoad: {
      mapTiler: !isSurftilerChartsEnabled,
    },
    isSurftilerChartsEnabled,
    surftilerFeatureFlags,
  };
};

export default ChartsHomepage;
